import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImageUrl } from "../lib/api";

/**
 * Full-screen image lightbox with prev/next/swipe/keyboard navigation.
 */
export default function Lightbox({ images, startIndex = 0, onClose, title }) {
  const [idx, setIdx] = useState(startIndex);
  const total = images.length;

  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, prev, next]);

  // Touch swipe
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const dx = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(dx) > 50) {
      if (dx > 0) prev();
      else next();
    }
    setTouchStart(null);
  };

  if (!images || total === 0) return null;
  const src = resolveImageUrl(images[idx]);

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[60] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(26,25,24,0.96)" }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Backdrop click closes */}
      <button
        aria-label="Chiudi"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-5 sm:px-10 py-5 z-10 pointer-events-none">
        <p className="font-sub text-[0.7rem] sm:text-[0.78rem] uppercase tracking-[0.22em] text-white/70 truncate max-w-[60%]">
          {title}
        </p>
        <div className="flex items-center gap-5 pointer-events-auto">
          {total > 1 && (
            <span className="font-sub text-[0.7rem] uppercase tracking-[0.22em] text-white/70">
              {idx + 1} / {total}
            </span>
          )}
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Chiudi"
            className="p-2 text-white hover:text-[color:var(--tr-accent)] transition-colors"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Prev / Next buttons (hidden on very small screens, replaced by swipe) */}
      {total > 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={prev}
            aria-label="Immagine precedente"
            className="hidden sm:flex absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center text-white hover:text-[color:var(--tr-accent)] transition-colors"
          >
            <ChevronLeft className="w-7 h-7" strokeWidth={1.25} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={next}
            aria-label="Immagine successiva"
            className="hidden sm:flex absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center text-white hover:text-[color:var(--tr-accent)] transition-colors"
          >
            <ChevronRight className="w-7 h-7" strokeWidth={1.25} />
          </button>
        </>
      )}

      {/* Image */}
      <img
        key={src}
        src={src}
        alt={title || ""}
        data-testid="lightbox-image"
        className="relative z-[5] max-w-[94vw] max-h-[82vh] sm:max-h-[86vh] w-auto h-auto object-contain select-none tr-rise"
        style={{ animationDuration: "500ms" }}
        draggable={false}
      />

      {/* Thumbnails strip (sm+) */}
      {total > 1 && (
        <div className="hidden sm:flex absolute bottom-6 inset-x-0 z-10 justify-center gap-2 px-6 overflow-x-auto no-scrollbar">
          {images.map((u, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`relative w-16 h-16 shrink-0 border ${
                i === idx ? "border-white" : "border-white/20 opacity-60 hover:opacity-100"
              } transition-opacity`}
              aria-label={`Vai a immagine ${i + 1}`}
            >
              <img src={resolveImageUrl(u)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
